/**
 * Servicio para comunicación con la API de autenticación
 */
import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import {
  ApiResponse,
  AuthSessionRequest,
  AuthSessionResponse,
  LoginRequest,
  LoginResponse,
  RecoverPasswordRequest,
  RecoverPasswordResponse,
  RefreshTokenRequest,
  RefreshTokenResponse,
  RegisterRequest,
  RegisterResponse,
  ValidateRegisterRequest
} from '../../../core/models/auth.model';

@Injectable({
  providedIn: 'root'
})
export class AuthApiService {
  private apiUrl = `${environment.apiUrl}/auth`;

  constructor(private http: HttpClient) {}

  /**
   * Inicia sesión con credenciales
   * @param request Datos de inicio de sesión
   * @returns Observable con la respuesta del login
   */
  login(request: LoginRequest): Observable<LoginResponse> {
    return this.http.post<LoginResponse>(`${this.apiUrl}/login`, request);
  }

  /**
   * Refresca el token de acceso
   * @param request Datos para refrescar el token
   * @returns Observable con los nuevos tokens
   */
  refreshToken(request: RefreshTokenRequest): Observable<RefreshTokenResponse> {
    return this.http.post<RefreshTokenResponse>(`${this.apiUrl}/refresh`, request);
  }

  /**
   * Valida los datos de registro (email, teléfono, documento) sin crear usuario.
   * Debe llamarse antes de crear el usuario en Firebase.
   */
  validateRegister(request: ValidateRegisterRequest): Observable<ApiResponse<null>> {
    return this.http.post<ApiResponse<null>>(`${this.apiUrl}/validate-register`, request);
  }

  /**
   * Registra un nuevo usuario
   * @param request Datos de registro
   * @returns Observable con la respuesta del registro
   */
  register(request: RegisterRequest): Observable<ApiResponse<RegisterResponse>> {
    return this.http.post<ApiResponse<RegisterResponse>>(`${this.apiUrl}/register`, request);
  }

  /**
   * Sincroniza la sesión del usuario autenticado en Firebase con el backend
   * @param request Datos para sincronizar sesión
   * @returns Observable con el perfil resumido y token de perfil
   */
  syncSession(request: AuthSessionRequest): Observable<ApiResponse<AuthSessionResponse>> {
    return this.http.post<ApiResponse<AuthSessionResponse>>(`${this.apiUrl}/session`, request);
  }

  /**
   * Solicita recuperación de contraseña
   * @param request Datos para recuperar contraseña
   * @returns Observable con la respuesta
   */
  recoverPassword(request: RecoverPasswordRequest): Observable<RecoverPasswordResponse> {
    return this.http.post<RecoverPasswordResponse>(`${this.apiUrl}/recover-password`, request);
  }

  /**
   * Cierra sesión en el servidor
   * @returns Observable con la respuesta
   */
  logout(): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/logout`, {});
  }

  /**
   * Obtiene el perfil del usuario actual
   * @returns Observable con los datos del usuario
   */
  getUserProfile(): Observable<any> {
    return this.http.get<any>(`${this.apiUrl}/profile`);
  }

  /**
   * Obtiene la sesión actual usando el profileToken
   * El interceptor AuthInterceptor agregará automáticamente el token
   * @returns Observable con la sesión actualizada
   */
  getCurrentSession(): Observable<ApiResponse<AuthSessionResponse>> {
    // El interceptor AuthInterceptor agregará automáticamente el profileToken
    return this.http.get<ApiResponse<AuthSessionResponse>>(`${this.apiUrl}/session`);
  }
}
